import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { 
  User, Mail, Phone, Store, MapPin, Building2, CreditCard, 
  Banknote, Lock, Shield, Edit2, Save, X, Camera, 
  CheckCircle, AlertCircle, Globe, Clock, Award, TrendingUp
} from 'lucide-react';

const Settings = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('profile');
    const [showSuccess, setShowSuccess] = useState(false);
    
    const [profile, setProfile] = useState({
        username: '',
        email: '',
        phone: '',
        storeName: '',
        storeAddress: '',
        storeDescription: '',
        gstNumber: '',
        accountHolderName: '',
        accountNumber: '',
        ifscCode: '',
        bankName: ''
    });

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                let token = localStorage.getItem('token') || sessionStorage.getItem('token');
                if (!token) {
                    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
                    token = userInfo.token;
                }

                if (!token) {
                    toast.error("Please login");
                    return navigate('/account');
                }

                const res = await axios.get('/api/seller/profile', {
                    headers: { Authorization: `Bearer ${token}` }
                });

                const data = res.data.profile;
                setProfile({
                    username: data.username || '',
                    email: data.email || '',
                    phone: data.phone || '',
                    storeName: data.storeName || '',
                    storeAddress: data.storeAddress || '',
                    storeDescription: data.storeDescription || '',
                    gstNumber: data.gstNumber || '',
                    accountHolderName: data.bankDetails?.accountHolderName || '',
                    accountNumber: data.bankDetails?.accountNumber || '',
                    ifscCode: data.bankDetails?.ifscCode || '',
                    bankName: data.bankDetails?.bankName || ''
                });
            } catch (error) {
                console.error('Error fetching profile:', error);
                toast.error('Failed to load profile');
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, [navigate]);

    const handleChange = (e) => {
        setProfile({ ...profile, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            let token = localStorage.getItem('token') || sessionStorage.getItem('token');
            if (!token) {
                const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
                token = userInfo.token;
            }

            await axios.put('/api/seller/profile', profile, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setShowSuccess(true);
            toast.success("Profile updated successfully!");
            setTimeout(() => setShowSuccess(false), 3000);
        } catch (error) {
            console.error('Error updating profile:', error);
            toast.error(error.response?.data?.message || 'Failed to update profile');
        } finally {
            setSaving(false);
        }
    };

    const tabs = [
        { id: 'profile', label: 'Profile Info', icon: User },
        { id: 'store', label: 'Store Settings', icon: Store },
        { id: 'bank', label: 'Bank Details', icon: Banknote },
        { id: 'security', label: 'Security', icon: Shield }
    ];

    if (loading) {
        return (
            <div className="settings-loading">
                <div className="spinner-settings"></div>
                <p>Loading your profile...</p>
            </div>
        );
    }

    return (
        <div className="settings-page">
            {/* Header */}
            <div className="settings-header">
                <div className="header-left">
                    <h1 className="page-title-settings">Profile & Settings</h1>
                    <p className="page-subtitle-settings">Manage your account, store, and banking information</p>
                </div>
                {showSuccess && (
                    <div className="success-badge">
                        <CheckCircle size={16} />
                        Changes saved successfully!
                    </div>
                )}
            </div>

            {/* Tabs */}
            <div className="settings-tabs">
                {tabs.map(tab => {
                    const Icon = tab.icon;
                    return (
                        <button
                            key={tab.id}
                            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            <Icon size={18} />
                            {tab.label}
                        </button>
                    );
                })}
            </div>

            <form onSubmit={handleSubmit}>
                {/* Profile Tab */}
                {activeTab === 'profile' && (
                    <div className="settings-card animate-fadeIn">
                        <div className="card-header-settings">
                            <div className="header-info">
                                <User size={20} />
                                <div>
                                    <h5 className="card-title">Personal Information</h5>
                                    <p className="card-subtitle">Update your personal details and contact information</p>
                                </div>
                            </div>
                        </div>
                        <div className="card-body-settings">
                            <div className="profile-avatar-section">
                                <div className="avatar-wrapper">
                                    <div className="avatar-placeholder">
                                        <span className="avatar-initials">
                                            {profile.username.charAt(0).toUpperCase()}
                                        </span>
                                    </div>
                                    <button type="button" className="avatar-upload-btn">
                                        <Camera size={16} />
                                    </button>
                                </div>
                                <div className="avatar-info">
                                    <h6>{profile.username}</h6>
                                    <p>Seller since 2024</p>
                                </div>
                            </div>

                            <div className="form-grid">
                                <div className="form-group-settings">
                                    <label className="form-label-settings">
                                        <User size={14} />
                                        Username
                                    </label>
                                    <input 
                                        type="text" 
                                        name="username" 
                                        value={profile.username} 
                                        onChange={handleChange} 
                                        className="form-control-settings" 
                                        required 
                                    />
                                </div>
                                <div className="form-group-settings">
                                    <label className="form-label-settings">
                                        <Mail size={14} />
                                        Email Address
                                    </label>
                                    <input 
                                        type="email" 
                                        name="email" 
                                        value={profile.email} 
                                        className="form-control-settings disabled" 
                                        disabled 
                                    />
                                    <span className="field-note">Email cannot be changed</span>
                                </div>
                                <div className="form-group-settings">
                                    <label className="form-label-settings">
                                        <Phone size={14} />
                                        Phone Number
                                    </label>
                                    <input 
                                        type="tel" 
                                        name="phone" 
                                        value={profile.phone} 
                                        onChange={handleChange} 
                                        className="form-control-settings" 
                                        required 
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Store Tab */}
                {activeTab === 'store' && (
                    <div className="settings-card animate-fadeIn">
                        <div className="card-header-settings">
                            <div className="header-info">
                                <Store size={20} />
                                <div>
                                    <h5 className="card-title">Store Information</h5>
                                    <p className="card-subtitle">Manage your store details and business information</p>
                                </div>
                            </div>
                        </div>
                        <div className="card-body-settings">
                            <div className="form-grid">
                                <div className="form-group-settings full-width">
                                    <label className="form-label-settings">
                                        <Store size={14} />
                                        Store Name
                                    </label>
                                    <input 
                                        type="text" 
                                        name="storeName" 
                                        value={profile.storeName} 
                                        onChange={handleChange} 
                                        className="form-control-settings" 
                                        required 
                                    />
                                </div>
                                <div className="form-group-settings full-width">
                                    <label className="form-label-settings">
                                        <MapPin size={14} />
                                        Store Address
                                    </label>
                                    <textarea 
                                        name="storeAddress" 
                                        value={profile.storeAddress} 
                                        onChange={handleChange} 
                                        className="form-control-settings" 
                                        rows="3"
                                    ></textarea>
                                </div>
                                <div className="form-group-settings full-width">
                                    <label className="form-label-settings">
                                        <Building2 size={14} />
                                        Store Description
                                    </label>
                                    <textarea 
                                        name="storeDescription" 
                                        value={profile.storeDescription} 
                                        onChange={handleChange} 
                                        className="form-control-settings" 
                                        rows="4"
                                        placeholder="Tell customers about your store and the plants you offer..."
                                    ></textarea>
                                </div>
                                <div className="form-group-settings">
                                    <label className="form-label-settings">
                                        <Award size={14} />
                                        GST Number (Optional)
                                    </label>
                                    <input 
                                        type="text" 
                                        name="gstNumber" 
                                        value={profile.gstNumber} 
                                        onChange={handleChange} 
                                        className="form-control-settings" 
                                        placeholder="22AAAAA0000A1Z"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Bank Tab */}
                {activeTab === 'bank' && (
                    <div className="settings-card animate-fadeIn">
                        <div className="card-header-settings">
                            <div className="header-info">
                                <Banknote size={20} />
                                <div>
                                    <h5 className="card-title">Bank Account Details</h5>
                                    <p className="card-subtitle">Manage your payout preferences and banking information</p>
                                </div>
                            </div>
                        </div>
                        <div className="card-body-settings">
                            <div className="bank-notice">
                                <AlertCircle size={16} />
                                <span>Your payout will be sent to this bank account. Please ensure the details are accurate.</span>
                            </div>
                            <div className="form-grid">
                                <div className="form-group-settings full-width">
                                    <label className="form-label-settings">
                                        <User size={14} />
                                        Account Holder Name
                                    </label>
                                    <input 
                                        type="text" 
                                        name="accountHolderName" 
                                        value={profile.accountHolderName} 
                                        onChange={handleChange} 
                                        className="form-control-settings" 
                                    />
                                </div>
                                <div className="form-group-settings">
                                    <label className="form-label-settings">
                                        <CreditCard size={14} />
                                        Account Number
                                    </label>
                                    <input 
                                        type="text" 
                                        name="accountNumber" 
                                        value={profile.accountNumber} 
                                        onChange={handleChange} 
                                        className="form-control-settings" 
                                    />
                                </div>
                                <div className="form-group-settings">
                                    <label className="form-label-settings">
                                        <Building2 size={14} />
                                        IFSC Code
                                    </label>
                                    <input 
                                        type="text" 
                                        name="ifscCode" 
                                        value={profile.ifscCode} 
                                        onChange={handleChange} 
                                        className="form-control-settings text-uppercase" 
                                    />
                                </div>
                                <div className="form-group-settings">
                                    <label className="form-label-settings">
                                        <Banknote size={14} />
                                        Bank Name
                                    </label>
                                    <input 
                                        type="text" 
                                        name="bankName" 
                                        value={profile.bankName} 
                                        onChange={handleChange} 
                                        className="form-control-settings" 
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Security Tab */}
                {activeTab === 'security' && (
                    <div className="settings-card animate-fadeIn">
                        <div className="card-header-settings">
                            <div className="header-info">
                                <Shield size={20} />
                                <div>
                                    <h5 className="card-title">Security Settings</h5>
                                    <p className="card-subtitle">Manage your password and security preferences</p>
                                </div>
                            </div>
                        </div>
                        <div className="card-body-settings">
                            <div className="security-options">
                                <div className="security-option">
                                    <div className="security-icon">
                                        <Lock size={20} />
                                    </div>
                                    <div className="security-info">
                                        <h6>Change Password</h6>
                                        <p>Update your password to keep your account secure</p>
                                    </div>
                                    <button type="button" className="security-btn" onClick={() => toast.info('Password reset feature coming soon')}>
                                        Change
                                    </button>
                                </div>
                                <div className="security-option">
                                    <div className="security-icon">
                                        <Shield size={20} />
                                    </div>
                                    <div className="security-info">
                                        <h6>Two-Factor Authentication</h6>
                                        <p>Add an extra layer of security to your account</p>
                                    </div>
                                    <button type="button" className="security-btn" onClick={() => toast.info('2FA feature coming soon')}>
                                        Enable
                                    </button>
                                </div>
                                <div className="security-option">
                                    <div className="security-icon">
                                        <Globe size={20} />
                                    </div>
                                    <div className="security-info">
                                        <h6>Session Management</h6>
                                        <p>Manage active sessions and logged-in devices</p>
                                    </div>
                                    <button type="button" className="security-btn" onClick={() => toast.info('Session management coming soon')}>
                                        Manage
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="settings-actions">
                    <button type="button" className="btn-cancel-settings" onClick={() => navigate('/seller/dashboard')}>
                        Cancel
                    </button>
                    <button type="submit" className="btn-save-settings" disabled={saving}>
                        {saving ? (
                            <>
                                <div className="spinner-small"></div>
                                Saving...
                            </>
                        ) : (
                            <>
                                <Save size={18} />
                                Save Changes
                            </>
                        )}
                    </button>
                </div>
            </form>

            <style>{`.settings-page {
  padding: 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  min-height: 100vh;
}

.settings-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 28px;
  flex-wrap: wrap;
  gap: 16px;
}

.page-title-settings {
  font-size: 28px;
  font-weight: 700;
  background: linear-gradient(135deg, #1a472a 0%, #2d6a4f 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 4px;
}

.page-subtitle-settings {
  color: #64748b;
  font-size: 14px;
  margin: 0;
}

.success-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  background: #d1fae5;
  border-radius: 40px;
  color: #10b981;
  font-size: 13px;
  font-weight: 500;
  animation: slideIn 0.3s ease;
}

.settings-tabs {
  display: flex;
  gap: 12px;
  margin-bottom: 28px;
  background: white;
  padding: 8px;
  border-radius: 60px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid #e8ecef;
  flex-wrap: wrap;
}

.tab-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 28px;
  background: transparent;
  border: none;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  transition: all 0.3s ease;
}

.tab-btn.active {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.tab-btn:hover:not(.active) {
  background: #f1f5f9;
  color: #10b981;
}

.settings-card {
  background: white;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid #e8ecef;
  margin-bottom: 24px;
}

.card-header-settings {
  padding: 24px;
  border-bottom: 1px solid #e8ecef;
  background: #fafcfc;
}

.header-info {
  display: flex;
  align-items: center;
  gap: 14px;
}

.header-info svg {
  color: #10b981;
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px;
}

.card-subtitle {
  font-size: 13px;
  color: #64748b;
  margin: 0;
}

.card-body-settings {
  padding: 28px;
}

.profile-avatar-section {
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 1px solid #e8ecef;
}

.avatar-wrapper {
  position: relative;
}

.avatar-placeholder {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-initials {
  font-size: 32px;
  font-weight: 700;
  color: white;
}

.avatar-upload-btn {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 32px;
  height: 32px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.avatar-upload-btn:hover {
  background: #f1f5f9;
  transform: scale(1.1);
}

.avatar-info h6 {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px;
}

.avatar-info p {
  font-size: 13px;
  color: #64748b;
  margin: 0;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
}

.form-group-settings.full-width {
  grid-column: span 2;
}

.form-group-settings {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-label-settings {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #1e293b;
}

.form-control-settings {
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  font-size: 14px;
  transition: all 0.3s ease;
  background: white;
}

.form-control-settings:focus {
  outline: none;
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

.form-control-settings.disabled {
  background: #f8fafc;
  color: #94a3b8;
}

.field-note {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 4px;
}

.bank-notice {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #fef3c7;
  border-radius: 12px;
  margin-bottom: 24px;
  font-size: 13px;
  color: #d97706;
}

.security-options {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.security-option {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: #f8fafc;
  border-radius: 16px;
  transition: all 0.3s ease;
}

.security-option:hover {
  background: #f1f5f9;
}

.security-icon {
  width: 48px;
  height: 48px;
  background: white;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #10b981;
}

.security-info {
  flex: 1;
}

.security-info h6 {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px;
}

.security-info p {
  font-size: 12px;
  color: #64748b;
  margin: 0;
}

.security-btn {
  padding: 8px 20px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 40px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.security-btn:hover {
  border-color: #10b981;
  color: #10b981;
}

.settings-actions {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 24px;
}

.btn-cancel-settings {
  padding: 12px 32px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 40px;
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-cancel-settings:hover {
  border-color: #ef4444;
  color: #ef4444;
}

.btn-save-settings {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 36px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border: none;
  border-radius: 40px;
  font-size: 14px;
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
}

.btn-save-settings:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.3);
}

.btn-save-settings:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.settings-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60vh;
  gap: 16px;
}

.spinner-settings {
  width: 40px;
  height: 40px;
  border: 3px solid #e2e8f0;
  border-top-color: #10b981;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@media (max-width: 768px) {
  .settings-page {
    padding: 16px;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-group-settings.full-width {
    grid-column: span 1;
  }

  .settings-tabs {
    border-radius: 20px;
  }

  .tab-btn {
    flex: 1;
    justify-content: center;
    padding: 10px 16px;
  }

  .profile-avatar-section {
    flex-direction: column;
    text-align: center;
  }

  .security-option {
    flex-direction: column;
    text-align: center;
  }

  .settings-actions {
    flex-direction: column;
  }

  .btn-cancel-settings,
  .btn-save-settings {
    width: 100%;
    justify-content: center;
  }
}`}</style>
        </div>
    );
};

export default Settings;